import { boundingBox } from '../../common/pdfReport';
import { extentOf } from '../../common/utils';

/**
 * Describes a bathymetry analysis as a document, for {@link renderReport} to lay out.
 * Everything in here is a pure function of what the result page already holds, which is what lets
 * the whole report be checked without a browser or an Earth Engine credential. The one impure part
 * - reading the map thumbnails - stays at the call site.
 * The sections follow the order someone reads a result in: what area, from which imagery, with
 * which model, how well it did, and then the maps. The metrics and the depth bands are written with
 * the same labels and the same rounding the on-screen panel uses, so a number quoted from the PDF
 * and a number read off the screen cannot disagree.
 */

/**
 * @constant REPORT_MODULE
 * @description Module key, used to name the file and to register the export.
 * @memberof module:Bathymetry
 */
export const REPORT_MODULE = 'bathymetry';

/**
 * @function depthRangeOf
 * @description The shallowest and deepest surveyed depth in the training data, which is the range
 * the model was actually calibrated over and therefore the range its predictions can be trusted in.
 * @param {Array<Array<Number>>} [matrix] - `[x, y, depth]` rows.
 * @memberof module:Bathymetry
 * @returns {String|null}
 */
export const depthRangeOf = (matrix) => {
  const depths = (matrix || []).map((row) => Number(row?.[2])).filter((depth) => Number.isFinite(depth));
  if (!depths.length) return null;
  const [shallowest, deepest] = extentOf(depths);
  return `${shallowest.toFixed(2)} m -> ${deepest.toFixed(2)} m`;
};

/**
 * @function metricRows
 * @description The metrics panel as report rows, in the order the panel shows them and with the
 * same labels and rounding.
 * @param {Object} [metrics] - The `metricsReport` object.
 * @param {Function} label - `(key) => String`, the panel's own translator.
 * @param {Function} format - `(key, value) => String`, the panel's own formatter.
 * @memberof module:Bathymetry
 * @returns {Array<Array<String>>}
 */
export const metricRows = (metrics, label, format) =>
  Object.entries(metrics || {})
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .map(([key, value]) => [label(key), format(key, value)]);

/**
 * @function depthBinTable
 * @description The error-by-depth-band table, as columns and rows.
 * The bands are the model's error against the surveyed depth, so a band with few points says more
 * about the survey than about the model - which is why the count is a column and not a footnote.
 * @param {Array<Object>} [bins] - `{ label, n, rmse, mae, bias }` entries.
 * @param {Object} headings - Column headings, already translated.
 * @memberof module:Bathymetry
 * @returns {Object} A table for {@link renderReport}.
 */
export const depthBinTable = (bins, headings) => {
  /**
   * `Number(null)` is 0, and 0 is finite, so a plain `isFinite` check turns a band that has no
   * error into a band reporting no error - the one reading a table like this must never be given.
   * An absent value stays absent and the layout writes a dash.
   */
  const metre = (value) =>
    value === null || value === undefined || value === '' || !Number.isFinite(Number(value))
      ? null
      : Number(value).toFixed(3);

  return {
    columns: [headings.band, headings.count, headings.rmse, headings.mae, headings.bias],
    rows: (bins || []).map((bin) => [bin.label, bin.n, metre(bin.rmse), metre(bin.mae), metre(bin.bias)]),
  };
};

/**
 * @function overallRmse
 * @description The RMSE over every evaluated pixel, as the report's metrics hold it.
 * Repeated on the depth-band table because that table is where a reader's eye goes: the bands make
 * the worst numbers prominent, and without the overall figure beside them a reader takes the
 * extremes of the shallowest and deepest bands for the model's error. Those bands are also the
 * thinnest, so they are the least reliable part of the table they dominate.
 * @param {Object} [metrics] - The `metricsReport` object.
 * @memberof module:Bathymetry
 * @returns {Number|null}
 */
export const overallRmse = (metrics) => {
  const value = Number(metrics?.CV_RMSE ?? metrics?.rmse ?? metrics?.RMSE);
  return Number.isFinite(value) ? value : null;
};

/**
 * @function depthBinChart
 * @description The depth-band table as an XY line, plotted at each band's midpoint.
 * The table states where the model is reliable; the curve shows it. A reader finds the depth at
 * which the error turns far faster from the shape than by comparing thirteen rows.
 * @param {Array<Object>} [bins] - `{ label, n, rmse }` entries.
 * @param {Object} text - `{ xLabel, yLabel, caption }`, already translated.
 * @memberof module:Bathymetry
 * @returns {Object} A chart for {@link renderReport}.
 */
export const depthBinChart = (bins, text) => ({
  points: (bins || [])
    .map((bin) => {
      /**
       * The band is plotted at its middle, which is the depth the error in it belongs to.
       * The numbers are matched unsigned on purpose: a label reads "0-2 m", and allowing a sign
       * makes the separating hyphen part of the second number, so the band came out centred on -1.
       */
      const bounds = String(bin?.label ?? '').match(/\d+(?:\.\d+)?/g);
      const x = bounds?.length >= 2 ? (Number(bounds[0]) + Number(bounds[1])) / 2 : Number(bounds?.[0]);
      /** `Number(null)` is 0, so a band with no error would otherwise be plotted as no error. */
      const rmse = bin?.rmse;
      const y = rmse === null || rmse === undefined || rmse === '' ? NaN : Number(rmse);
      return { x, y };
    })
    .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y)),
  xLabel: text.xLabel,
  yLabel: text.yLabel,
  caption: text.caption,
});

/**
 * @function staleTrainingWarning
 * @description Warns when the surveyed depths and the imagery are years apart.
 * A model is fitted on the assumption that the sea bed the survey measured is the sea bed the
 * image sees. Over a few years in a mobile coastal system that stops being true, and the error the
 * report prints is then measuring the change as if it were the model's fault. Nothing else in the
 * report would say so: both dates are shown, in different sections, and nobody subtracts them.
 * @param {String} [surveyDate] - The soundings' date.
 * @param {String} [imageDate] - The imagery's date or period; a period uses its start.
 * @param {Number} limitYears - How far apart they may be before this is worth saying.
 * @param {Function} warn - Translated wording, taking `{{survey}}`, `{{image}}` and `{{years}}`.
 * @memberof module:Bathymetry
 * @returns {String|null}
 */
export const staleTrainingWarning = (surveyDate, imageDate, limitYears, warn) => {
  /**
   * `new Date(null)` is the epoch, not an invalid date, so a missing survey date would otherwise
   * produce a confident warning that the data is fifty years old.
   */
  if (!surveyDate || !imageDate) return null;
  const survey = new Date(surveyDate);
  const image = new Date(String(imageDate).split('/')[0]);
  if (Number.isNaN(survey.getTime()) || Number.isNaN(image.getTime())) return null;

  const years = Math.abs(image - survey) / (365.25 * 24 * 60 * 60 * 1000);
  if (years < limitYears) return null;
  return warn({
    survey: survey.toISOString().slice(0, 10),
    image: image.toISOString().slice(0, 10),
    years: years.toFixed(1),
  });
};

/**
 * @function thinBandWarning
 * @description Warns when some depth bands rest on very few points.
 * @param {Array<Object>} [bins] - The depth bands.
 * @param {Number} limit - Below this many points a band is called thin.
 * @param {Function} warn - Translated wording, taking `{{bands}}` and `{{limit}}`.
 * @memberof module:Bathymetry
 * @returns {String|null}
 */
export const thinBandWarning = (bins, limit, warn) => {
  const thin = (bins || []).filter((bin) => Number.isFinite(Number(bin?.n)) && Number(bin.n) < limit);
  if (!thin.length) return null;
  return warn({ bands: thin.map((bin) => bin.label).join(', '), limit });
};

/**
 * @function buildBathymetryReport
 * @description The whole document, as data.
 * @param {Object} params
 * @param {Object} params.data - The acquisition state: model, bands, imagery, tree parameters.
 * @param {Object} params.fileData - The uploaded inputs: area, CRS, soundings, water mask.
 * @param {Object} params.metrics - The `metricsReport` shown on screen.
 * @param {Array<Object>} params.bins - The depth bands shown under it.
 * @param {Array<Object>} params.images - Already loaded by {@link loadImageForReport}.
 * @param {Object} params.text - Every piece of wording, already translated by the caller. Passed
 *  in rather than read here so this module stays pure and the report follows the language the user
 *  has the app in.
 * @param {Function} params.metricLabel - The panel's label lookup.
 * @param {Function} params.metricFormat - The panel's value formatter.
 * **Called by:
 * @see {ProcessingPage} - Bathymetry's result page.
 * @memberof module:Bathymetry
 * @returns {Object} A document description for {@link renderReport}.
 */
export const buildBathymetryReport = ({ data, fileData, metrics, bins, images, text, metricLabel, metricFormat }) => {
  const mission = data?.missionInfo;
  const treeParams = data?.treeParams;
  const soundings = fileData?.trainingMatrix?.length;
  const rmse = overallRmse(metrics);

  /**
   * Things the numbers above do not say on their own. Each one is a reason a figure in this report
   * might mean less than it appears to, which is worth a line here rather than a surprise later.
   */
  const warnings = [
    staleTrainingWarning(fileData?.date, data?.imageDate, text.staleYears, text.staleWarning),
    thinBandWarning(bins, text.thinLimit, text.thinWarning),
  ].filter(Boolean);

  return {
    title: text.title,
    subtitle: text.subtitle,
    footer: text.footer,
    missingImageNote: text.missingImage,
    sections: [
      {
        heading: text.areaHeading,
        rows: [
          [text.areaFile, fileData?.kmlFileName],
          [text.areaExtent, boundingBox(fileData?.coordinates)],
          [text.crs, fileData?.coordinatesSystem],
          [text.soundingsFile, fileData?.fileName],
          [text.soundings, soundings],
          [text.surveyedRange, depthRangeOf(fileData?.trainingMatrix)],
          [text.surveyDate, fileData?.date],
        ],
      },
      {
        heading: text.imageryHeading,
        rows: [
          [text.mission, mission?.name],
          [text.resolution, mission?.opticalResolution ? `${mission.opticalResolution} m` : null],
          [text.processing, data?.typeImageProcess],
          [text.period, data?.imageDate],
          [text.waterMask, fileData?.waterMaskMethod],
          [text.waterThreshold, fileData?.threshold],
          [text.imageCount, data?.selectedImageDates?.length ? String(data.selectedImageDates.length) : null],
          [text.imageList, data?.selectedImageDates?.length ? data.selectedImageDates.join(', ') : null],
        ],
      },
      {
        heading: text.modelHeading,
        rows: [
          [text.model, data?.modelSelected],
          [text.bands, data?.bands?.filter(Boolean).join(', ')],
          [text.extinctionDepth, data?.depthLightExtinction ? `${data.depthLightExtinction} m` : null],
          [text.trainingReducer, data?.trainingDataReducer],
          [text.validationSplit, data?.validationSplit ? text.validationSplitOn : text.validationSplitOff],
          [text.predictorSet, treeParams?.predictorSet],
          [text.seed, treeParams?.seed],
        ],
        note: data?.presetMode ? text.presetNote : null,
      },
      {
        heading: text.metricsHeading,
        rows: metricRows(metrics, metricLabel, metricFormat),
        note: text.metricsNote,
      },
      {
        heading: text.binsHeading,
        rows: [[text.overallRmse, rmse === null ? null : `${rmse.toFixed(3)} m`]],
        table: depthBinTable(bins, text.binColumns),
        chart: depthBinChart(bins, text.binChart),
        note: bins?.length ? text.binsNote : null,
      },
      {
        heading: text.warningsHeading,
        rows: warnings.map((warning, index) => [`${index + 1}`, warning]),
        note: warnings.length ? text.warningsNote : null,
      },
      {
        heading: text.mapsHeading,
        images,
      },
    ],
  };
};
